import { makeStyles } from '@mui/styles'
import React, { useState } from 'react'

import lang from '@/lang'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import TodoFileList from '@/modules/todo/components/todo-files/todo-file-list'
import FileUploadChooser from '@/components/controls/file-upload-chooser'
import ThemeInterface from '@/theme-interface'
import { File as CustomFile } from '@/modules/signature/types'
import { FILE_LIST_FILTER } from '@/modules/base/util/constants'
import { setFolder } from '@/redux/ducks/document/list'

import DocumentsUploadTrigger from '@/modules/document/components/upload/documents-upload-trigger'
import { dispatch } from '@/redux/store'
import { isDmsUploadEnabled, createDmsUploader, createDmsUploadHandler } from '@/redux/ducks/document/uploadHelpers'
import InfoTooltip from './todo-form-file-upload-info'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  todoFileList: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  fileChooser: {
    backgroundColor: theme.backgroundColor,
    height: 190,
    '& .title': {
      fontSize: 14,
      padding: 0,
      margin: 0,
    },
    '& .subTitle': {
      fontSize: 12,
      lineHeight: 'unset',
    },
    '& .special': {
      fontWeight: 400,
    },
  },
  uploadWrapper: {
    display: 'contents',
  },
  fileTypes: {
    fontSize: 12,
    color: theme.textMuted,
    marginTop: 10,
    maxWidth: 300,
  },
}))

interface TodoFormFileUploadProps {
  uploader: {
    methods: {
      addFiles: (files: CustomFile[]) => void;
    };
  };
  onSelect: (file: CustomFile) => void;
  onPreview: () => void;
  type: string;
  hasFile: boolean;
  onUploadComplete?: () => void;
  tenantCollaborationId;
}

const TodoFormFileUpload = (props: TodoFormFileUploadProps) => {
  const classes = useStyles()
  const { uploader, onSelect, onPreview, tenantCollaborationId } = props
  const [containerOpen, setContainerOpen] = useState<boolean>(false)
  const useDmsUpload = isDmsUploadEnabled()

  const openContainer = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    setContainerOpen(true)
    setFolder({})
  }

  const closeContainer = (): void => {
    setContainerOpen(false)
  }

  const handleFileSelect = (file: CustomFile): void => {
    closeContainer()
    onSelect?.(file)
  }

  const handleUploadSuccess = (fileObjects: CustomFile[]) => {
    fileObjects.forEach(file => {
      // create temporary url from file blob for preview
      handleFileSelect({ ...file, url: URL.createObjectURL(file.file as unknown as Blob) })
    })
  }

  const handleDmsUpload = createDmsUploadHandler(dispatch, handleUploadSuccess)
  const dmsUploader = createDmsUploader(handleDmsUpload, uploader)

  const renderFileList = () => {
    if (!containerOpen) return null

    return (
      <TodoFileList
        tenantCollaborationId={tenantCollaborationId}
        filter={FILE_LIST_FILTER.NO_SYSTEM_DOCUMENTS}
        onSelect={handleFileSelect}
        onClose={closeContainer}
        onPreview={onPreview}
      />
    )
  }

  const fileUploadChooserProps = {
    multiple: true,
    uploader: useDmsUpload ? dmsUploader : uploader,
    className: classes.fileChooser,
    onOpenFileContainer: openContainer,
    useDmsUpload,
  }

  const documentUploadTriggerProps = {
    multiple: true,
    disabled: false,
    onSubmit: (files, e) => handleDmsUpload(files, e),
  }

  const fileUploadChooserContent = (
    <FileUploadChooser {...fileUploadChooserProps} >
      <div className={classes.fileTypes}>
        <InfoTooltip />
      </div>
    </FileUploadChooser>
  )

  const fileUploadChooser = useDmsUpload
    ? (<DocumentsUploadTrigger {...documentUploadTriggerProps}>
      {fileUploadChooserContent}
    </DocumentsUploadTrigger>)
    : fileUploadChooserContent

  return (
    <div className={classes.uploadWrapper}>
      <SimpleDrawer
        className={classes.todoFileList}
        open={containerOpen}
        enableBackdropClick
        onClose={closeContainer}
        headline={lang.todo.uploader.title}
      >
        {renderFileList()}
      </SimpleDrawer>
      {fileUploadChooser}
    </div>
  )
}

export default TodoFormFileUpload
